import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, ArrowRight, Sparkles } from "lucide-react";
import { logDev } from "@/lib/log";

const LS_KEY = "ts_tour_seen_v1";

const STEPS = [
  {
    route: "/",
    target: '[data-testid="bnav-home"]',
    title: "Home & Announcements",
    body: "Semua update dari admin dan channel Telegram akan muncul di sini secara real-time. Tekan card slot coaching untuk book seat.",
  },
  {
    route: "/education",
    target: '[data-testid="bnav-education"]',
    title: "Education",
    body: "Download ebooks, tonton e-videos, dan apply booking untuk coaching session di tab Coaching.",
  },
  {
    route: "/performance",
    target: '[data-testid="bnav-performance"]',
    title: "Performance",
    body: "Signals live, history TP/SL, dan Trade Journal untuk track profit-loss bulanan.",
  },
];

/**
 * First-time onboarding tour — walks the user through Home → Education → Performance
 * by driving the bottom-nav and highlighting each tab. Skippable, shown once per browser.
 */
export default function OnboardingTour({ user }) {
  const [step, setStep] = useState(-1); // -1 = idle
  const [targetRect, setTargetRect] = useState(null);
  const nav = useNavigate();
  const loc = useLocation();

  // Start when authenticated and never seen. Only on mobile / tablet since
  // the tour walks users through the mobile bottom-nav — desktop uses the sidebar.
  useEffect(() => {
    if (!user) return;
    if (typeof window !== "undefined" && window.innerWidth >= 1024) return;
    let seen = false;
    try { seen = localStorage.getItem(LS_KEY) === "1"; } catch (err) { logDev(err); }
    if (seen) return;
    // Give the app a moment to render.
    const t = setTimeout(() => setStep(0), 900);
    return () => clearTimeout(t);
  }, [user]);

  const current = step >= 0 && step < STEPS.length ? STEPS[step] : null;

  // Navigate to the step route so the target is on screen.
  useEffect(() => {
    if (!current) return;
    if (loc.pathname !== current.route) nav(current.route);
  }, [current, loc.pathname, nav]);

  // Track the target's position for the spotlight ring + tooltip anchor.
  useEffect(() => {
    if (!current) { setTargetRect(null); return; }
    const measure = () => {
      const el = document.querySelector(current.target);
      if (el) {
        const r = el.getBoundingClientRect();
        setTargetRect({ x: r.left, y: r.top, w: r.width, h: r.height });
      } else {
        setTargetRect(null);
      }
    };
    // Wait one frame after route change.
    const t = setTimeout(measure, 250);
    window.addEventListener("resize", measure);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); };
  }, [current, loc.pathname]);

  const finish = () => {
    setStep(-1);
    try { localStorage.setItem(LS_KEY, "1"); } catch (err) { logDev(err); }
  };
  const next = () => (step + 1 < STEPS.length ? setStep(step + 1) : finish());

  // Tooltip position: above the target (bottom nav), or centered if unknown.
  const tooltip = useMemo(() => {
    if (!targetRect) return { top: "auto", bottom: 24, left: 16, right: 16 };
    const bottom = window.innerHeight - targetRect.y + 12;
    return { bottom, left: 16, right: 16, top: "auto" };
  }, [targetRect]);

  if (!current) return null;

  return (
    <div className="fixed inset-0 z-[60]" data-testid="onboarding-tour" role="dialog" aria-label="Onboarding tour">
      {/* Dim backdrop with a bright hole around the target */}
      <div className="absolute inset-0 bg-slate-950/60" onClick={finish} />
      {targetRect && (
        <div
          className="absolute pointer-events-none rounded-xl ring-4 ring-[#00b4ff] shadow-[0_0_0_9999px_rgba(2,6,23,0.55)]"
          style={{
            left: Math.max(2, targetRect.x - 6),
            top: Math.max(2, targetRect.y - 6),
            width: Math.min(window.innerWidth - 4 - Math.max(2, targetRect.x - 6), targetRect.w + 12),
            height: Math.min(window.innerHeight - 4 - Math.max(2, targetRect.y - 6), targetRect.h + 12),
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        data-testid="tour-tooltip"
        className="absolute rounded-2xl border border-slate-800 bg-slate-900 text-white p-5 shadow-[0_24px_60px_-8px_rgba(0,0,0,0.5)] sm:max-w-md sm:mx-auto"
        style={tooltip}
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#00b4ff]/15 grid place-items-center text-[#00b4ff] shrink-0">
            <Sparkles size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-mono-plex tracking-[0.28em] uppercase text-slate-400">
              Step {step + 1} of {STEPS.length}
            </div>
            <div className="mt-1 font-display font-bold text-base tracking-tight">{current.title}</div>
            <p className="mt-1.5 text-sm text-slate-300 leading-relaxed">{current.body}</p>
            <div className="mt-4 flex items-center justify-between gap-3">
              <button
                data-testid="tour-skip"
                onClick={finish}
                className="text-xs font-mono-plex tracking-widest uppercase text-slate-400 hover:text-white"
                style={{ transition: "color 0.15s ease" }}
              >
                Skip
              </button>
              <div className="flex items-center gap-1.5">
                {STEPS.map((s, i) => (
                  <span
                    key={s.route}
                    className={`w-1.5 h-1.5 rounded-full ${i === step ? "bg-[#00b4ff]" : "bg-slate-700"}`}
                  />
                ))}
              </div>
              <button
                data-testid="tour-next"
                onClick={next}
                className="inline-flex items-center gap-1.5 bg-[#00b4ff] hover:bg-[#0090cc] text-white text-xs font-mono-plex tracking-widest uppercase px-4 py-2 rounded-full"
                style={{ transition: "background-color 0.15s ease" }}
              >
                {step + 1 < STEPS.length ? "Next" : "Done"} <ArrowRight size={12} />
              </button>
            </div>
          </div>
          <button
            onClick={finish}
            aria-label="Close tour"
            className="text-slate-400 hover:text-white p-1 -mr-1 -mt-1"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
